import { memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VariableSizeList } from "react-window";
import { findIndex } from "lodash";
import { makeStyles } from "@mui/styles";

import {
  WAVE_EDIT_MODE_WARNING,
  COLLECTION_EDIT_TIMES_WARNING,
  EDIT_ONE_MODE_ACTIVE_WARNING,
  PROFILE_LOCKED_BY_WARNING,
  ROUTE_DAY_LOCKED_BY_WARNING,
  CLEAR_ROUTE_WARNING_TITLE,
  CLEAR_ROUTE_WARNING_DELIVERIES,
  CLEAR_ROUTE_WARNING_SAME_DAY_COLLECTIONS,
  CLEAR_ROUTE_WARNING_PLANNED_COLLECTIONS,
  CLEAR_ROUTE_WARNING_OVERRIDES,
  CLEAR_ROUTE_WARNING_LOCATIONS,
  CLEAR_ROUTE_WARNING_PICKUP,
  PROFILE_LOCKED_IN_OTHER_BROWSER,
} from "../../consts/strings";
import { SAME_DAY } from "../../consts/collections";
import { ROUTE_CARD } from "../../consts/sentryIds";
import { ROUTE_CARD_PADDING } from "../../consts/routeDetails";
import {
  COLLECTIONS_PLAN_TYPE,
  DELIVERY_PLAN_TYPES,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
} from "../../consts/planTypes";

import { routeHasTerritory } from "../../utils/routes";

import {
  collectionTimeEditSelector,
  selectedRouteSelector,
  selectedCollectionTabSelector,
} from "../../reducers/ownApp/selectors";
import {
  getMatrixProfileType,
  getSelectedPlanDay,
  isDailyPlanSelector,
  isEditOneModeSelector,
  isWaveEditModeSelector,
  matrixProfileLockSelector,
  routeDayLocksSelector,
  routeInEditSelector,
  routesImpactedSelector,
  volumeDataSelector,
} from "../../reducers/matrixProfile/selectors";
import { addNotification } from "../../reducers/ownApp";
import {
  clearRoute,
  editRoute,
  editDriver,
} from "../../reducers/matrixProfile/thunkActions";
import { onRouteCLick } from "../../reducers/ownApp/thunkActions";

import { CompactCard } from "../RouteCard/CompactCard";
import { View } from "../View";
import { ConfirmModal } from "../Modals/ConfirmModal/ConfirmModal";

const useStyles = makeStyles(({ typography }) => ({
  item: {
    marginTop: typography.pxToRem(2),
  },
  noScrollbar: {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
}));

export const RouteList = memo(({ routes, drivers, vehicles, height = 900 }) => {
  const listRef = useRef();
  const selectedRoute = useSelector(selectedRouteSelector);
  const volumeData = useSelector(volumeDataSelector);
  const styles = useStyles();
  const dispatch = useDispatch();
  const isDaily = useSelector(isDailyPlanSelector);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const isWaveEditMode = useSelector(isWaveEditModeSelector);
  const routeInEdit = useSelector(routeInEditSelector);
  const matrixProfileLock = useSelector(matrixProfileLockSelector);
  const routeDayLocks = useSelector(routeDayLocksSelector);
  const routesImpacted = useSelector(routesImpactedSelector);
  const collectionEditTimesMode = useSelector(collectionTimeEditSelector);
  const planType = useSelector(getMatrixProfileType);
  const dayOfWeek = useSelector(getSelectedPlanDay);
  const collectionType = useSelector(selectedCollectionTabSelector);
  const [routeToClear, setRouteToClear] = useState(null);
  const [scrollOffset, setScrollOffset] = useState(0);

  useEffect(() => {
    if (!selectedRoute || !routes) return;
    listRef.current.resetAfterIndex(0, true);
    let selectedIndex = findIndex(
      routes,
      (route) => route.routeCode === selectedRoute.routeCode
    );
    if (selectedIndex < 0) return;
    listRef.current.scrollToItem(selectedIndex, "start");
  }, [routes, selectedRoute]);

  useEffect(() => {
    listRef?.current.resetAfterIndex(0, true);
  }, [routes, volumeData]);

  const onRouteClicked = (route) => {
    dispatch(onRouteCLick(route));
  };

  const onEditClick = (route) => {
    const routeDayLock = routeDayLocks[route.routeCode];

    // TODO error on lockedByYouInDifferentBrowser

    let warningMessage;
    if (matrixProfileLock.lockedByUserCode && !matrixProfileLock.lockedByYou)
      warningMessage = PROFILE_LOCKED_BY_WARNING(
        matrixProfileLock.lockedByUserName
      );
    else if (matrixProfileLock.lockedInDifferentBrowser)
      warningMessage = PROFILE_LOCKED_IN_OTHER_BROWSER;
    else if (routeDayLock?.lockedByUserCode && !routeDayLock.lockedByYou)
      warningMessage = ROUTE_DAY_LOCKED_BY_WARNING({
        routeCode: route.routeCode,
        dayOfWeek,
        lockedByUserName: routeDayLock.lockedByUserName,
      });
    else if (isEditOneMode && route.routeCode !== routeInEdit?.routeCode)
      warningMessage = EDIT_ONE_MODE_ACTIVE_WARNING(routeInEdit?.routeCode);
    else if (isWaveEditMode) warningMessage = WAVE_EDIT_MODE_WARNING;
    else if (collectionEditTimesMode)
      warningMessage = COLLECTION_EDIT_TIMES_WARNING;

    if (warningMessage)
      dispatch(
        addNotification({
          message: warningMessage,
          options: { variant: "info" },
        })
      );
    else if (!isEditOneMode) dispatch(editRoute({ route }));
  };

  const onClearClick = async (route) => {
    const routeDayLock = routeDayLocks[route.routeCode];
    let warningMessage;
    if (matrixProfileLock.lockedByUserCode && !matrixProfileLock.lockedByYou)
      warningMessage = PROFILE_LOCKED_BY_WARNING(
        matrixProfileLock.lockedByUserName
      );
    else if (matrixProfileLock.lockedInDifferentBrowser)
      warningMessage = PROFILE_LOCKED_IN_OTHER_BROWSER;
    else if (routeDayLock?.lockedByUserCode && !routeDayLock.lockedByYou)
      warningMessage = ROUTE_DAY_LOCKED_BY_WARNING({
        routeCode: route.routeCode,
        dayOfWeek,
        lockedByUserName: routeDayLock.lockedByUserName,
      });
    else if (isEditOneMode && routeInEdit?.routeCode !== route.routeCode)
      warningMessage = EDIT_ONE_MODE_ACTIVE_WARNING(routeInEdit?.routeCode);
    else if (isWaveEditMode) warningMessage = WAVE_EDIT_MODE_WARNING;
    else if (collectionEditTimesMode)
      warningMessage = COLLECTION_EDIT_TIMES_WARNING;

    if (warningMessage)
      return dispatch(
        addNotification({
          message: warningMessage,
          options: { variant: "info" },
        })
      );
    if (routeHasTerritory(route, planType, collectionType))
      setRouteToClear(route);
    else
      dispatch(
        addNotification({
          message: "This route is empty",
          options: { variant: "info" },
        })
      );
  };

  const getConfirmModalMessage = () => {
    if (planType === OVERRIDES_PLAN_TYPE)
      return CLEAR_ROUTE_WARNING_OVERRIDES(routeToClear);
    else if (planType === PICKUP_PLAN_TYPE)
      return CLEAR_ROUTE_WARNING_PICKUP(routeToClear);
    else if (planType === LOCATIONS_PLAN_TYPE)
      return CLEAR_ROUTE_WARNING_LOCATIONS(routeToClear);
    else if (planType !== COLLECTIONS_PLAN_TYPE)
      return CLEAR_ROUTE_WARNING_DELIVERIES(routeToClear);
    else if (collectionType === SAME_DAY)
      return CLEAR_ROUTE_WARNING_SAME_DAY_COLLECTIONS(routeToClear);
    else return CLEAR_ROUTE_WARNING_PLANNED_COLLECTIONS(routeToClear);
  };

  const onEditDriverClick = async (route) => {
    let warningMessage;
    // Daily plan - driver edit allowed if plan is NOT locked by you.
    // Template/Dated plan - Driver edit is allowed if template is locked by you.
    if (
      isDaily
        ? matrixProfileLock.lockedByYou
        : matrixProfileLock.lockedByUserCode && !matrixProfileLock.lockedByYou
    )
      warningMessage = PROFILE_LOCKED_BY_WARNING(
        matrixProfileLock.lockedByUserName
      );
    else if (isEditOneMode)
      warningMessage = EDIT_ONE_MODE_ACTIVE_WARNING(routeInEdit?.routeCode);
    else if (isWaveEditMode) warningMessage = WAVE_EDIT_MODE_WARNING;
    else if (collectionEditTimesMode)
      warningMessage = COLLECTION_EDIT_TIMES_WARNING;

    if (warningMessage)
      dispatch(
        addNotification({
          message: warningMessage,
          options: { variant: "info" },
        })
      );
    else dispatch(editDriver({ route }));
  };

  const renderRow = ({ index, style }) => {
    const route = routes[index];
    const driver = drivers[route?.driverCode];
    const vehicle = vehicles[driver?.vehicleType];
    return (
      <View
        className={styles.item}
        key={index}
        style={style}
        id={`route-card-${index}`}
        sentryId={`${ROUTE_CARD}__${index}`}
      >
        <CompactCard
          route={route}
          driver={driver}
          vehicle={vehicle}
          onClick={() => onRouteClicked(route)}
          onEdit={() => onEditClick(route)}
          onClear={() => onClearClick(route)}
          onEditDriverClick={() => onEditDriverClick(route)}
          selected={
            route.routeCode === selectedRoute?.routeCode ||
            routesImpacted[route.routeCode]
          }
          scrollOffset={scrollOffset}
        />
      </View>
    );
  };

  const getItemSize = (index) => {
    const route = routes[index];
    const hasTerritory = !!routeHasTerritory(route, planType, collectionType);
    if (!volumeData) return 100 + ROUTE_CARD_PADDING;
    else if (planType === OVERRIDES_PLAN_TYPE)
      return (hasTerritory ? 140 : 120) + ROUTE_CARD_PADDING;
    else if (
      DELIVERY_PLAN_TYPES.includes(planType) &&
      route.overridesAllocated &&
      route.locationRoute
    )
      return 160 + ROUTE_CARD_PADDING;
    else return (hasTerritory ? 160 : 140) + ROUTE_CARD_PADDING;
  };

  return (
    <>
      {Array.isArray(routes) && (
        <VariableSizeList
          onScroll={({ scrollOffset }) => setScrollOffset(scrollOffset)}
          className={styles.noScrollbar}
          ref={listRef}
          height={height}
          itemCount={routes.length}
          itemSize={getItemSize}
          width={314}
        >
          {renderRow}
        </VariableSizeList>
      )}
      {routeToClear && (
        <ConfirmModal
          open={true}
          title={CLEAR_ROUTE_WARNING_TITLE}
          content={getConfirmModalMessage()}
          onClose={() => setRouteToClear(null)}
          onContinue={async () => {
            let error;
            if (!isEditOneMode)
              ({ error } = await dispatch(editRoute({ route: routeToClear })));
            if (!error) dispatch(clearRoute(routeToClear));
          }}
        />
      )}
    </>
  );
});

export default RouteList;
